import React, { useState, useMemo, useEffect } from 'react';

// --- IMPORTACIÓN DE NUESTRAS UTILIDADES DE SEGUROS ---
import { 
    formatDateShort, 
    formatCurrencyUniversal, 
    parseCurrency, 
    formatCurrencyForInput 
} from '../../../../utils/segurosUtils';

// --- ICONOS LOCALES ---
const IconTable = () => (
    <svg className="w-4 h-4 text-[#01032d]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const IconChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
);

const IconChevronRight = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
);

const IconInfo = () => (
    <svg className="w-12 h-12 text-slate-350 stroke-[1.5] opacity-60 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

const MONETARY_KEYS = [
    'importe', 'premio', 'primaAdelanto', 'primaEmitida', 'primaPrimeraCuota', 
    'importeReclamado', 'primaAnulacion', 'primaEmitidaRefacturacion', 'premioRefacturacion'
];

const BADGE_KEYS = [
    'estadoEmision', 'estado', 'estadoGestion', 'estadoRefacturacion', 'estadoGestionRenovacion',
    'auditoriaRenovacion', 'tieneDeuda', 'tieneSiniestro', 'pagoEfectuado', 'estadoPoliza', 'estadoContrato'
];

const DATE_KEYS = [
    'fechaCarga', 'fechaCargaCobranza', 'fechaCargaSiniestro', 'fechaCargaEndoso', 
    'fechaCargaRefacturaciones', 'fechaAnulacion', 'inicioVigencia', 'finVigencia', 
    'fechaRefacturacion', 'inicioVigenciaRefac', 'finVigenciaRefac', 'fechaSiniestro', 
    'fechaCobro', 'fechaCobroRefacturacion', 'fechaReclamo', 'fechaReclamoRefacturacion', 
    'fechaDespacho'
];

const TablePreview = ({ registros, columnasSeleccionadas, camposDisponibles, moduloRaiz }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(100);

    useEffect(() => {
        setCurrentPage(1);
    }, [registros, moduloRaiz, recordsPerPage]);

    const paginatedRegistros = useMemo(() => {
        const start = (currentPage - 1) * recordsPerPage;
        return registros.slice(start, start + recordsPerPage);
    }, [registros, currentPage, recordsPerPage]);

    const totalPages = Math.ceil(registros.length / recordsPerPage);

    const formatCellValue = (key, value, row) => {
        if (value === undefined || value === null || value === '') return '-';

        // 1. Reutilización premium de formatCurrencyForInput de segurosUtils (Formato local sin signo $)
        if (key === 'masaSalarial') {
            return (
                <span className="font-mono font-bold text-slate-700 tracking-tight">
                    {formatCurrencyForInput(value)}
                </span>
            );
        }

        // 2. Badges Semánticos de Estado
        if (BADGE_KEYS.includes(key)) {
            const stringVal = String(value).toUpperCase().trim();

            if (['tieneDeuda', 'tieneSiniestro'].includes(key)) {
                const isYes = value === true || stringVal === 'SÍ' || stringVal === 'SI';
                return isYes ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-100">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
                        SÍ
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200/50">
                        NO
                    </span>
                );
            }

            if (key === 'pagoEfectuado') {
                const isYes = value === true || stringVal === 'SÍ' || stringVal === 'SI';
                return isYes ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
                        SÍ
                    </span>
                ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-100">
                        NO
                    </span>
                );
            }

            const isGreen = ['VIGENTE', 'PAGADO', 'EMITIDO', 'ACTIVO', 'RENOVACIÓN EMITIDA', 'NORMAL', 'VIGENTE ACTIVA', 'SI', 'SÍ', 'VIGENTE / ACTIVA'].some(s => stringVal.includes(s));
            const isRed = ['VENCIDA', 'ANULADA', 'CANCELADA', 'RECHAZADA', 'CRÍTICO', 'NO GESTIONADA', 'ATRASADO', 'VENCIDA SIN REVISIÓN', 'PUNTO CIEGO', 'VENCIDO'].some(s => stringVal.includes(s));
            const isYellow = ['PENDIENTE', 'EN PROCESO', 'NO GESTIONADA', 'NO INICIADA', 'RECLAMADO', 'REFACTURACION', 'REFACTURACIÓN', 'NO INICIADO'].some(s => stringVal.includes(s));

            if (isGreen) {
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse"></span>
                        {stringVal}
                    </span>
                );
            } else if (isRed) {
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-150">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1.5 animate-pulse"></span>
                        {stringVal}
                    </span>
                );
            } else if (isYellow) {
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-50 text-amber-800 border border-amber-150">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5 animate-pulse"></span>
                        {stringVal}
                    </span>
                );
            }

            return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                    {stringVal}
                </span>
            );
        }

        // 3. Celdas Financieras en USD estricto (Utiliza el formato centralizado)
        if (MONETARY_KEYS.includes(key)) {
            let refMoneda = row.moneda || '$';
            if (['primaEmitidaRefacturacion', 'premioRefacturacion'].includes(key)) {
                refMoneda = row.monedaRefacturacion || '$';
            } else if (key === 'importe') {
                refMoneda = row.monedaCobranza || row.moneda || '$';
            }
            return (
                <span className="font-mono font-bold text-slate-800 tracking-tight">
                    {formatCurrencyUniversal(value, refMoneda)}
                </span>
            );
        }

        // 4. Fechas utilizando formatDateShort de Utils
        if (DATE_KEYS.includes(key)) {
            return <span className="font-mono text-slate-500 font-semibold">{formatDateShort(value)}</span>;
        }

        if (key === 'nPoliza' || key === 'clienteDni' || key === 'clienteCuit' || key === 'nContrato') {
            return <span className="font-mono font-bold text-slate-700">{value}</span>;
        }

        return String(value);
    };

    return (
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm overflow-hidden flex flex-col h-full text-left">
            <div className="p-4 px-6 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                    <IconTable /> 
                    <span className="text-[10px] font-black text-[#01032d] uppercase tracking-widest">
                        Datos Procesados ({registros.length} registros totales)
                    </span>
                </div>
                
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Filas por página:</span>
                    <select
                        value={recordsPerPage}
                        onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                        className="bg-white border border-slate-200 rounded-lg p-1 px-2 text-[11px] font-bold text-slate-600 outline-none cursor-pointer hover:border-slate-350 transition-colors"
                    >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                    </select>
                </div>
            </div>

            <div className="flex-grow overflow-auto custom-scrollbar relative bg-white">
                <table className="w-full text-xs table-auto border-collapse min-w-max">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] font-black border-b tracking-wider sticky top-0 z-20 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                        <tr>
                            {columnasSeleccionadas.map(colKey => {
                                let defCampo;
                                Object.values(camposDisponibles).forEach(seccion => {
                                    const match = seccion.find(c => c.key === colKey);
                                    if (match) defCampo = match;
                                });
                                const labelCabecera = defCampo ? defCampo.label : colKey;
                                const isNumeric = MONETARY_KEYS.includes(colKey) || ['masaSalarial', 'capitas', 'alicuota', 'porcentajeComision'].includes(colKey);

                                return (
                                    <th 
                                        key={colKey} 
                                        className={`p-3.5 px-5 font-black bg-slate-50 text-slate-500 select-none truncate ${
                                            isNumeric ? 'text-right' : 'text-left'
                                        }`}
                                    >
                                        {labelCabecera}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="font-semibold text-slate-700 divide-y divide-slate-100 bg-white">
                        {paginatedRegistros.length === 0 ? (
                            <tr>
                                <td colSpan={columnasSeleccionadas.length || 1} className="p-16 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <IconInfo />
                                        <p className="text-slate-500 font-bold uppercase tracking-wider text-xs">No se encontraron registros</p>
                                        <p className="text-[11px] text-slate-400 mt-1 max-w-sm mx-auto leading-relaxed">
                                            Prueba cambiando los parámetros de segmentación temporal o removiendo filtros del panel lateral para expandir la cobertura de datos.
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedRegistros.map((row, idx) => (
                                <tr key={row.id || idx} className="hover:bg-slate-50/40 transition-colors duration-150">
                                    {columnasSeleccionadas.map(colKey => {
                                        const isNumeric = MONETARY_KEYS.includes(colKey) || ['masaSalarial', 'capitas', 'alicuota', 'porcentajeComision'].includes(colKey);
                                        return (
                                            <td 
                                                key={colKey} 
                                                className={`p-3 px-5 truncate ${isNumeric ? 'text-right' : 'text-left'}`} 
                                                title={row[colKey] !== undefined ? String(row[colKey]) : ''}
                                            >
                                                {formatCellValue(colKey, row[colKey], row)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="h-14 shrink-0 bg-slate-50 border-t border-slate-200/80 flex justify-between items-center px-8 text-xs font-bold text-slate-500">
                    <span className="text-slate-450">
                        Mostrando página <span className="text-slate-700 font-black">{currentPage}</span> de <span className="text-slate-700 font-black">{totalPages}</span> (<span className="text-slate-600">{paginatedRegistros.length}</span> de <span className="text-slate-600">{registros.length}</span> filas)
                    </span>
                    <div className="flex items-center gap-2">
                        <button 
                            type="button"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                            disabled={currentPage === 1} 
                            className="px-3.5 py-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 active:scale-95"
                        >
                            <IconChevronLeft /> Anterior
                        </button>
                        
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum = i + 1;
                                if (currentPage > 3 && totalPages > 5) {
                                    pageNum = currentPage - 3 + i;
                                    if (pageNum + (4 - i) > totalPages) {
                                        pageNum = totalPages - 4 + i;
                                    }
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        type="button"
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                                            currentPage === pageNum 
                                                ? 'bg-[#01032d] text-white' 
                                                : 'bg-white hover:bg-slate-100 text-slate-600 border border-slate-200'
                                        }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <button 
                            type="button"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                            disabled={currentPage === totalPages} 
                            className="px-3.5 py-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white uppercase tracking-wider text-[10px] font-black transition-all shadow-sm flex items-center gap-1 active:scale-95"
                        >
                            Siguiente <IconChevronRight />
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { height: 8px; width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            `}</style>
        </div>
    );
};

export default TablePreview;